// src/hooks/useGameLogic.js
import { useState, useEffect, useCallback, useRef } from 'react';
import { socket, serverUrl } from '../socket';
import { applySegment, initFrom } from '../drawStore';

export function useGameLogic(pseudo) {
  // états connexion & rôle
  const [connected, setConnected] = useState(false);
  const [gameStarted, setGameStarted] = useState(false);
  const [isChef, setIsChef] = useState(false);
  const [chefName, setChefName] = useState('');

  // état de la manche
  const [roundNumber, setRoundNumber] = useState(0);
  const [phase, setPhase] = useState('');
  const [timeLeft, setTimeLeft] = useState(0);       // ← compte-à-rebours
  const [guessOptions, setGuessOptions] = useState([]);
  const [guessAvatars, setGuessAvatars] = useState({});
  const [guessNames, setGuessNames] = useState({});
  const [hasGuessed, setHasGuessed] = useState(false);
  const [myGuess, setMyGuess] = useState(null);
  const [scores, setScores] = useState({});
  const scoresRef = useRef({});
  const [playersGuessed, setPlayersGuessed] = useState([]);

  // mode "mashup" : extrait audio + crans de guess + sync horloge serveur
  const [clipUrl, setClipUrl] = useState(null);
  const [clipDurationSec, setClipDurationSec] = useState(0);
  const [guessTicks, setGuessTicks] = useState([]); // [{ pseudo, fraction, avatarUrl }]
  const [clipSync, setClipSync] = useState(null); // { startAt, serverNow, leadInMs, tailMs }
  const [clipProgress, setClipProgress] = useState(0); // position 0..1 poussee par le serveur
  const clipRoundRef = useRef(0); // manche courante : sert a ignorer les clipProgress en retard d'une manche precedente
  const [lastMashupName, setLastMashupName] = useState(null); // nom du mashup (reveal)
  const [lastMashupAuthor, setLastMashupAuthor] = useState(null); // auteur du mashup (reveal)
  const [lastCorrectAnswer, setLastCorrectAnswer] = useState(null); // cle de la bonne reponse (audio)
  // mode "chrono"
  const [chronoMessage, setChronoMessage] = useState(null); // { author, avatarUrl } (entete)
  const [chronoMessages, setChronoMessages] = useState([]);  // messages du burst reveles progressivement
  const [chronoPeriod, setChronoPeriod] = useState(null);   // { min, max }
  const [lastAnswerFraction, setLastAnswerFraction] = useState(null); // position de la bonne date (reveal)
  // mode "blindtest"
  const [blindtestPool, setBlindtestPool] = useState([]); // titres pour la recherche fuzzy
  const [blindtestMaxAttempts, setBlindtestMaxAttempts] = useState(2);
  // zone de dessin collaborative
  const [drawEnabled, setDrawEnabled] = useState(false);
  const [drawClearInfo, setDrawClearInfo] = useState(null); // { votes, needed }
  // acces aux modes (pilote par l'owner)
  const [modeAccess, setModeAccessState] = useState(null);
  // mode "studio" (recreer un mashup, multipiste)
  const [studioAssign, setStudioAssign] = useState(null);     // roue : { pool, landsOn, author, wheelMs, audioUrl }
  const [studioInfo, setStudioInfo] = useState(null);         // { mashupName, mashupAuthor, audioUrl, loopSec }
  const [studioItems, setStudioItems] = useState([]);         // rating : [{ id, mashupName }]
  const [studioRanking, setStudioRanking] = useState(null);   // reveal : [{ pseudo, audioUrl, avatarUrl, mashupName, total, rank }]
  const [creationProgress, setCreationProgress] = useState([]); // pseudos ayant soumis
  // mode "discussion" (Fibbage)
  const [discussionContext, setDiscussionContext] = useState(null); // { context, contextAuthor, avatarUrl }
  const [discussionOptions, setDiscussionOptions] = useState([]);   // [{ id, text, mine }]
  const [discussionReveal, setDiscussionReveal] = useState(null);   // { options, realReply, gained, ... }
  const [discussionProgress, setDiscussionProgress] = useState([]); // pseudos ayant soumis/vote
  // mode "whoisit" (retrouver le prenom)
  const [whoContext, setWhoContext] = useState([]);         // [{ anon, text }]
  const [whoKey, setWhoKey] = useState(null);               // { anon, text (censure) }
  const [whoReveal, setWhoReveal] = useState(null);         // { answer, key, results, roundPoints }
  // mode "coverDraw" (dessine une cover)
  const [coverAssign, setCoverAssign] = useState(null);     // { pool, landsOn, author, wheelMs }
  const [coverDraw, setCoverDraw] = useState(null);         // { name, author, audioUrl, drawSec, startAt }
  const [coverReveal, setCoverReveal] = useState(null);     // { index, total, author, imageDataUrl, searchPool, guessSec }
  const [coverResult, setCoverResult] = useState(null);     // { author, name, correctGuessers, upvotes, needed, becameOfficial, imageDataUrl }
  const [coverUpvote, setCoverUpvote] = useState({ votes: 0, needed: 0 });
  // mode choisi par le chef en lobby (synchro live, titre + theme)
  const [lobbyMode, setLobbyMode] = useState(null);

  // affichage
  const [messages, setMessages] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [chatMessages, setChatMessages] = useState([]);

  // **nouveau** : on stocke l’auteur de la manche qui vient de se terminer
  const [lastAuthor, setLastAuthor] = useState(null);
  // on conserve aussi les propositions de la manche
  const [lastProposals, setLastProposals] = useState({});
  // points gagnés lors de la dernière manche
  const [lastRoundPoints, setLastRoundPoints] = useState({});
  // paramètres envoyés par le serveur lors du démarrage
  const [gameSettings, setGameSettings] = useState(null);
  const [currentRoom, setCurrentRoom] = useState('');
  const [finalRanking, setFinalRanking] = useState([]);
  const [eloChanges, setEloChanges] = useState(null); // { pseudo: { delta, elo } }
  // Derniere demande de join (pour re-join auto apres reconnexion) + garde 1er connect
  const lastJoinRef = useRef(null);
  const connectedOnceRef = useRef(false);
  const [datasetReady, setDatasetReady] = useState(false);
  const [datasetInfo, setDatasetInfo] = useState(null);
  const [datasetAuthors, setDatasetAuthors] = useState([]); // [{label, name, count}]
  const [detectedNames, setDetectedNames] = useState([]);   // prenoms auto-detectes (mode "C'est qui ?")
  const [keptAuthors, setKeptAuthorsState] = useState([]); // label[] : joueurs a deviner
  const [maxAuthors, setMaxAuthors] = useState(10);
  const [datasetPeriod, setDatasetPeriod] = useState(null); // { min, max } ms
  const [sources, setSources] = useState([]); // [{ id, type, label, authors, totalMessages }]
  // Upload CSV progress state
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0); // 0..100

  // store latest scores for callbacks
  useEffect(() => {
    scoresRef.current = scores;
  }, [scores]);

  // ─── TIMER : décrémente `timeLeft` chaque seconde ─────────────────────────
  useEffect(() => {
    if (timeLeft <= 0) return;
    const timerId = setTimeout(() => {
      setTimeLeft(prev => prev - 1);
    }, 1000);
    return () => clearTimeout(timerId);
  }, [timeLeft]);

  // ─── SOCKET EVENTS ────────────────────────────────────────────────────────
  useEffect(() => {
    // 1. État autoritatif de la room (participants, chef, dataset, etc.)
    function handleRoomState({ participants, chef, gameStarted: gs, datasetReady: ready, datasetMeta, datasetAuthors: dsAuthors, keptAuthors: kept, maxAuthors: maxA, datasetPeriod: dsPeriod, sources: srcs, lobbyMode: lm, detectedNames: dn }) {
      setConnected(true);
      setGameStarted(gs);
      // Retour lobby : on purge la phase/timer fantomes (sauf l'overlay de fin).
      if (!gs) { setPhase((p) => (p === 'ended' ? p : '')); setTimeLeft(0); }
      setIsChef(pseudo === chef);
      setChefName(chef || '');
      if (lm !== undefined) setLobbyMode(lm || null);
      setScores(Object.fromEntries((participants || []).map(p => [p, scoresRef.current[p] ?? 0])));
      setFinalRanking([]);
      setDatasetReady(!!ready);
      if (datasetMeta) setDatasetInfo(datasetMeta);
      if (Array.isArray(dsAuthors)) setDatasetAuthors(dsAuthors);
      if (Array.isArray(dn)) setDetectedNames(dn);
      if (Array.isArray(kept)) setKeptAuthorsState(kept);
      if (typeof maxA === 'number') setMaxAuthors(maxA);
      setDatasetPeriod(dsPeriod || null);
      if (Array.isArray(srcs)) setSources(srcs);
      setAnnouncements([
        `Participants: ${(participants || [])
          .map(p => (p === chef ? `👑${p}` : p))
          .join(', ')}`
      ]);
    }

    function handleDatasetUpdated({ authors, totalMessages, ready }) {
      setDatasetReady(!!ready);
      setDatasetInfo({ authors, totalMessages });
      setAnnouncements(a => [
        ...a,
        `Dataset imported (${authors} authors, ${totalMessages} messages)`
      ]);
    }

    // 2. Partie démarrée
    function handleGameStarted({ mode, roundsTotal, messagesPerRound, onlyGifs, allowedMedia, minMessageLength, thinkingTimeSec, sequentialMessages, clipSeconds, maxAttempts, searchPool, drawEnabled }) {
      setGameStarted(true);
      setGameSettings({ mode, roundsTotal, messagesPerRound, onlyGifs, allowedMedia, minMessageLength, thinkingTimeSec, sequentialMessages, clipSeconds, maxAttempts, drawEnabled });
      if (Array.isArray(searchPool)) setBlindtestPool(searchPool);
      if (typeof maxAttempts === 'number') setBlindtestMaxAttempts(maxAttempts);
      setDrawEnabled(!!drawEnabled);
      setFinalRanking([]);
    }

    // 3. Nouvelle manche : phase "Réflexion"
    function handleRoundStarted({ roundNumber: rn, scores: sc, guessOptions: go, phaseDuration, guessAvatars, guessNames, clipUrl: cu, clipDurationSec: cds, startAt, serverNow, leadInMs, tailMs, message, messages: chronoMsgs, period, alreadyGuessed }) {
      setRoundNumber(rn);
      clipRoundRef.current = rn; // reference pour filtrer les clipProgress en retard
  setPhase('thinking');
      setTimeLeft(phaseDuration);
      setScores(sc);
      setGuessOptions(go);
      setGuessAvatars(guessAvatars || {});
      setGuessNames(guessNames || {});
      setHasGuessed(!!alreadyGuessed);
      setMyGuess(null);
      setPlayersGuessed([]);
      setMessages([]);
      setAnnouncements([]);
      setLastAuthor(null);  // on réinitialise l’auteur précédent
      setLastProposals({});
      setLastRoundPoints({});
      // mode mashup : extrait de la manche + reset des crans + sync horloge
      setClipUrl(cu || null);
      setClipDurationSec(cds || 0);
      setGuessTicks([]);
      setClipProgress(0);
      setLastMashupName(null);
      setLastMashupAuthor(null);
      setLastCorrectAnswer(null);
      setLastAnswerFraction(null);
      // chrono : entete auteur + liste de messages du burst
      setChronoMessage(message || null);
      setChronoMessages(Array.isArray(chronoMsgs) ? chronoMsgs : (message?.text ? [message.text] : []));
      setChronoPeriod(period || null);
      if (startAt) setClipSync({ startAt, serverNow, leadInMs: leadInMs || 0, tailMs: tailMs || 0, recvAt: Date.now() });
      else setClipSync(null);
    }

    // Sync haute frequence de la position de l'extrait (pilote la jauge).
    function handleClipProgress({ round, t }) {
      // Ignore un event en retard d'une manche precedente (evite une jauge faussee au changement de manche).
      if (round != null && round !== clipRoundRef.current) return;
      if (typeof t === 'number') setClipProgress(t);
    }

    // Zone de dessin : segments collaboratifs.
    function handleDrawSegment(seg) { applySegment(seg); }
    function handleDrawInit({ segments }) { initFrom(segments || []); setDrawEnabled(true); setDrawClearInfo(null); }
    function handleDrawClearProgress({ votes, needed }) { setDrawClearInfo({ votes, needed }); }

    // Acces aux modes (owner config).
    function handleModeAccess({ config }) { setModeAccessState(config || null); }

    // Mode "studio" : wheel -> creating -> rating -> reveal.
    function handleStudioWheel(d) { setPhase('wheel'); setStudioAssign(d || null); }
    function handleStudioRound({ roundNumber: rn, scores: sc, mashupName, mashupAuthor, audioUrl, loopSec, createSec }) {
      setRoundNumber(rn); if (sc) setScores(sc);
      setPhase('creating'); setTimeLeft(createSec || 180);
      setStudioInfo({ mashupName, mashupAuthor, audioUrl, loopSec }); setStudioItems([]); setStudioRanking(null);
      setCreationProgress([]); setFinalRanking([]);
    }
    function handleCreationReceived({ pseudo: who }) {
      setCreationProgress((p) => (p.includes(who) ? p : [...p, who]));
    }
    function handleStudioRating({ items, rateSec }) {
      setPhase('rating'); setTimeLeft(rateSec || 45); setStudioItems(items || []);
    }
    function handleStudioReveal({ ranking, scores: sc }) {
      setPhase('reveal'); setStudioRanking(ranking || []); if (sc) setScores(sc);
    }

    // Mode "discussion" (Fibbage) : writing -> voting -> reveal.
    function handleDiscussionRound({ roundNumber: rn, scores: sc, context, targetAuthor, writeSec }) {
      setRoundNumber(rn); if (sc) setScores(sc);
      setPhase('writing'); setTimeLeft(writeSec || 45);
      setDiscussionContext({ messages: Array.isArray(context) ? context : [], target: targetAuthor || null });
      setDiscussionOptions([]); setDiscussionReveal(null); setDiscussionProgress([]); setFinalRanking([]);
    }
    function handleLieReceived({ pseudo: who }) { setDiscussionProgress((p) => (p.includes(who) ? p : [...p, who])); }
    function handleDiscussionOptions({ options, voteSec }) {
      setPhase('voting'); setTimeLeft(voteSec || 30); setDiscussionOptions(options || []); setDiscussionProgress([]);
    }
    function handleVoteReceived({ pseudo: who }) { setDiscussionProgress((p) => (p.includes(who) ? p : [...p, who])); }
    function handleDiscussionReveal(data) {
      setPhase('reveal'); setDiscussionReveal(data || null); if (data?.scores) setScores(data.scores);
    }

    // Mode "whoisit" : guessing -> reveal.
    function handleWhoRound({ roundNumber: rn, scores: sc, context, key, guessSec, alreadyGuessed }) {
      setRoundNumber(rn); if (sc) setScores(sc);
      setPhase('guessing'); setTimeLeft(guessSec || 35);
      setWhoContext(Array.isArray(context) ? context : []); setWhoKey(key || null); setWhoReveal(null);
      setHasGuessed(!!alreadyGuessed); setPlayersGuessed([]); setFinalRanking([]);
    }
    function handleWhoReveal(data) {
      setPhase('reveal'); setWhoReveal(data || null); if (data?.scores) setScores(data.scores);
    }

    // Mode "coverDraw" : wheel -> drawing -> reveal (par cover).
    function handleCoverAssignment(d) {
      setPhase('wheel'); setCoverAssign(d || null); setCoverDraw(null); setCoverReveal(null); setCoverResult(null);
    }
    function handleCoverDrawStart(d) {
      setPhase('drawing'); setCoverDraw(d || null); setTimeLeft(d?.drawSec || 240);
    }
    function handleCoverRevealNext(d) {
      setPhase('reveal'); setCoverReveal(d || null); setCoverResult(null);
      setCoverUpvote({ votes: 0, needed: 0 }); setTimeLeft(d?.guessSec || 25); setPlayersGuessed([]);
    }
    function handleCoverRevealResult(d) {
      setCoverResult(d || null); if (d?.scores) setScores(d.scores);
    }
    function handleUpvoteProgress(d) {
      setCoverUpvote({ votes: d?.votes || 0, needed: d?.needed || 0 });
    }

    // 4. Message révélé
    function handleMessageRevealed({ messagePart }) {
      setMessages(msgs => [
        ...msgs,
        { content: messagePart, timestamp: new Date(), type: 'chat' }
      ]);
    }

    // Mode chrono : message suivant du burst revele progressivement.
    function handleChronoReveal({ messagePart }) {
      if (typeof messagePart === 'string') setChronoMessages((m) => [...m, messagePart]);
    }

    function handlePlayerGuessed({ pseudo: guessedPseudo, fraction, avatarUrl }) {
      setPlayersGuessed(pg =>
        pg.includes(guessedPseudo) ? pg : [...pg, guessedPseudo]
      );
      // mode mashup : place/actualise le cran du joueur sur la timeline
      if (typeof fraction === 'number') {
        setGuessTicks(ts => {
          const rest = ts.filter(x => x.pseudo !== guessedPseudo);
          return [...rest, { pseudo: guessedPseudo, fraction, avatarUrl: avatarUrl || null }];
        });
      }
    }

    // 5. Fin de manche : phase "Résultat"
    function handleRoundEnded({ correctAuthor, correctAnswer, mashupName, mashupAuthor, answerFraction, proposals, scores: sc, resultDuration, roundPoints }) {
  setPhase('result');
      setTimeLeft(resultDuration);
      setScores(sc);
      setLastAuthor(correctAuthor);  // on conserve l’auteur (compat classic)
      if (mashupName !== undefined) setLastMashupName(mashupName || null);
      if (mashupAuthor !== undefined) setLastMashupAuthor(mashupAuthor || null);
      if (correctAnswer !== undefined) setLastCorrectAnswer(correctAnswer);
      if (answerFraction !== undefined) setLastAnswerFraction(answerFraction);
      setLastProposals(proposals);
      setLastRoundPoints(roundPoints || {});
      const roundAnnouncements = [
        `Author was ${correctAuthor}`,
        ...Object.entries(proposals)
          .filter(([, { guess }]) => guess === correctAuthor)
          .map(([player]) => `${player} guessed correctly!`)
      ];
      setAnnouncements(roundAnnouncements);
    }

    // 6. Transition entre manches
    function handleTransitionStarted({ nextRoundIn, correctAuthor }) {
  setPhase('transition');
      setTimeLeft(nextRoundIn);
  setAnnouncements([
   `Round ${roundNumber}, author was ${correctAuthor}.`,]);
    }

    // 7. Fin de partie
    function handleGameEnded({ finalRanking, eloChanges }) {
  setPhase('ended');
      setTimeLeft(0);
      setPlayersGuessed([]);
      setFinalRanking(finalRanking);
      setEloChanges(eloChanges || null);
      setAnnouncements([
        'Game ended! Final ranking:',
        ...finalRanking.map(r => `${r.pseudo} (${r.score})`)
      ]);
    }

    // 8. Lobby redémarré
    function handleLobbyRestarted({ participants, chef }) {
      setGameStarted(false);
      setPhase('');
      setRoundNumber(0);
      setScores(Object.fromEntries(participants.map(p => [p, 0])));
      setGuessOptions([]);
      setMessages([]);
      setPlayersGuessed([]);
      setAnnouncements([
        `Participants: ${participants.join(', ')}`,
        `Host: ${chef}`,
      ]);
      setIsChef(pseudo === chef);
      setGameSettings(null);
      setLastAuthor(null);
      setLastProposals({});
      setLastRoundPoints({});
      setFinalRanking([]);
    }

    // 8. Erreur serveur
    function handleErrorMessage(msg) {
  setAnnouncements(a => [...a, `Error: ${msg}`]);
    }

    function handleLobbyModeChanged({ mode }) {
      setLobbyMode(mode || null);
    }

    function handleChatMessage({ pseudo: author, message }) {
      setChatMessages(msgs => [
        ...msgs,
        { pseudo: author, message, timestamp: new Date() },
      ]);
    }

    // Enregistrement des handlers
    socket.on('roomData', handleRoomState); // compat initiale
    socket.on('roomState', handleRoomState);
    // compat: on garde playerLeft/chefChanged pour annonces, mais l'état vient de roomState
    socket.on('playerLeft', ({ pseudo: leftPseudo }) => {
      setAnnouncements(a => [...a, `${leftPseudo} left the room`]);
    });
    socket.on('chefChanged', ({ chef }) => {
      setChefName(chef || '');
      setIsChef(pseudo === chef);
      setAnnouncements(a => [...a, `New host: ${chef}`]);
    });
  socket.on('datasetUpdated', handleDatasetUpdated);
    socket.on('gameStarted', handleGameStarted);
    socket.on('roundStarted', handleRoundStarted);
    socket.on('clipProgress', handleClipProgress);
    socket.on('drawSegment', handleDrawSegment);
    socket.on('drawInit', handleDrawInit);
    socket.on('drawClearProgress', handleDrawClearProgress);
    socket.on('modeAccessConfig', handleModeAccess);
    socket.on('modeAccessChanged', handleModeAccess);
    socket.on('studioWheel', handleStudioWheel);
    socket.on('studioRound', handleStudioRound);
    socket.on('creationReceived', handleCreationReceived);
    socket.on('studioRating', handleStudioRating);
    socket.on('studioReveal', handleStudioReveal);
    socket.on('discussionRound', handleDiscussionRound);
    socket.on('lieReceived', handleLieReceived);
    socket.on('discussionOptions', handleDiscussionOptions);
    socket.on('voteReceived', handleVoteReceived);
    socket.on('discussionReveal', handleDiscussionReveal);
    socket.on('whoRound', handleWhoRound);
    socket.on('whoReveal', handleWhoReveal);
    socket.on('coverAssignment', handleCoverAssignment);
    socket.on('coverDrawStart', handleCoverDrawStart);
    socket.on('coverRevealNext', handleCoverRevealNext);
    socket.on('coverRevealResult', handleCoverRevealResult);
    socket.on('upvoteProgress', handleUpvoteProgress);
    socket.on('messageRevealed', handleMessageRevealed);
    socket.on('chronoReveal', handleChronoReveal);
    // Recupere la config d'acces des que possible.
    if (socket.connected) socket.emit('getModeAccess');
    socket.on('playerGuessed', handlePlayerGuessed);
    socket.on('roundEnded', handleRoundEnded);
    socket.on('transitionStarted', handleTransitionStarted);
    socket.on('gameEnded', handleGameEnded);
    socket.on('lobbyRestarted', handleLobbyRestarted);
    socket.on('lobbyModeChanged', handleLobbyModeChanged);
    socket.on('errorMessage', handleErrorMessage);
    socket.on('chatMessage', handleChatMessage);

    // Re-join automatique apres une (re)connexion (login Discord, coupure reseau)
    connectedOnceRef.current = socket.connected;
    function handleConnect() {
      if (connectedOnceRef.current && lastJoinRef.current) {
        socket.emit('joinRoom', lastJoinRef.current);
      }
      connectedOnceRef.current = true;
      socket.emit('getModeAccess');
    }
    socket.on('connect', handleConnect);

    // Cleanup
    return () => {
      socket.off('connect', handleConnect);
  socket.off('roomData', handleRoomState);
  socket.off('roomState', handleRoomState);
  socket.off('playerLeft');
  socket.off('chefChanged');
      socket.off('datasetUpdated', handleDatasetUpdated);
      socket.off('gameStarted', handleGameStarted);
      socket.off('roundStarted', handleRoundStarted);
      socket.off('clipProgress', handleClipProgress);
      socket.off('drawSegment', handleDrawSegment);
      socket.off('drawInit', handleDrawInit);
      socket.off('drawClearProgress', handleDrawClearProgress);
      socket.off('modeAccessConfig', handleModeAccess);
      socket.off('modeAccessChanged', handleModeAccess);
      socket.off('studioWheel', handleStudioWheel);
      socket.off('studioRound', handleStudioRound);
      socket.off('creationReceived', handleCreationReceived);
      socket.off('studioRating', handleStudioRating);
      socket.off('studioReveal', handleStudioReveal);
      socket.off('discussionRound', handleDiscussionRound);
      socket.off('lieReceived', handleLieReceived);
      socket.off('discussionOptions', handleDiscussionOptions);
      socket.off('voteReceived', handleVoteReceived);
      socket.off('discussionReveal', handleDiscussionReveal);
      socket.off('whoRound', handleWhoRound);
      socket.off('whoReveal', handleWhoReveal);
      socket.off('coverAssignment', handleCoverAssignment);
      socket.off('coverDrawStart', handleCoverDrawStart);
      socket.off('coverRevealNext', handleCoverRevealNext);
      socket.off('coverRevealResult', handleCoverRevealResult);
      socket.off('upvoteProgress', handleUpvoteProgress);
      socket.off('messageRevealed', handleMessageRevealed);
      socket.off('chronoReveal', handleChronoReveal);
      socket.off('playerGuessed', handlePlayerGuessed);
      socket.off('roundEnded', handleRoundEnded);
      socket.off('transitionStarted', handleTransitionStarted);
      socket.off('gameEnded', handleGameEnded);
      socket.off('lobbyRestarted', handleLobbyRestarted);
      socket.off('lobbyModeChanged', handleLobbyModeChanged);
      socket.off('errorMessage', handleErrorMessage);
      socket.off('chatMessage', handleChatMessage);
    };
  }, [pseudo, roundNumber, lastAuthor, chefName]);

  // ─── ÉMETTEURS VERS LE SERVEUR ────────────────────────────────────────────
  const joinRoom = useCallback(({ roomCode, pseudo: p, secret }) => {
    setCurrentRoom(roomCode);
    lastJoinRef.current = { roomCode, pseudo: p, secret };
    socket.emit('joinRoom', lastJoinRef.current);
  }, []);

  const startGame = useCallback((params = {}) => {
    socket.emit('startGame', params);
  }, []);

  // Chef : diffuse le mode choisi en lobby (titre + theme live chez tous).
  const selectMode = useCallback((mode) => {
    setLobbyMode(mode || null); // optimiste
    socket.emit('selectMode', { mode });
  }, []);

  // Zone de dessin : envoi d'un segment (le dessin local est deja applique).
  const emitDrawSegment = useCallback((seg) => { socket.emit('drawSegment', seg); }, []);
  const emitDrawClearVote = useCallback(() => { socket.emit('drawClearVote'); }, []);

  // Owner : (de)active un mode pour une typologie (admins/players).
  const setModeAccess = useCallback((mode, typology, value) => { socket.emit('setModeAccess', { mode, typology, value }); }, []);

  // Mode "studio" : soumission du mix (audio binaire) + note.
  const submitCreation = useCallback((audio, contentType, extractStart) => { socket.emit('submitCreation', { audio, contentType: contentType || 'audio/wav', extractStart }); }, []);
  const submitRating = useCallback((id, score) => { socket.emit('submitRating', { id, score }); }, []);

  // Mode "discussion" (Fibbage) : ecriture d'un bluff + vote.
  const submitLie = useCallback((text) => { socket.emit('submitLie', { text }); }, []);
  const submitVote = useCallback((id) => { socket.emit('submitVote', { id }); }, []);

  // Mode "coverDraw" : soumission de la cover dessinee + upvote au reveal.
  const submitCover = useCallback((dataUrl) => { socket.emit('submitCover', { dataUrl }); }, []);
  const submitUpvote = useCallback(() => { socket.emit('submitUpvote'); }, []);

  const stopGame = useCallback(() => {
    socket.emit('stopGame');
  }, []);

  const uploadCsv = useCallback(async (csvText, label) => {
    try {
      if (!currentRoom) throw new Error('Room non définie');
      const baseUrl = serverUrl;

      // Utilise XMLHttpRequest pour suivre la progression d'upload
      setUploading(true);
      setUploadProgress(0);

      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', `${baseUrl}/api/rooms/${currentRoom}/csv`, true);
        xhr.setRequestHeader('Content-Type', 'text/plain');
        xhr.setRequestHeader('x-chef-id', socket.id || '');
        if (label) { try { xhr.setRequestHeader('x-source-label', encodeURIComponent(label)); } catch { /* ignore */ } }

        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) {
            const pct = Math.round((e.loaded / e.total) * 100);
            setUploadProgress(pct);
          } else {
            // Si la taille n'est pas calculable, afficher une progression indéterminée (on laisse le pourcentage tel quel)
            setUploadProgress((p) => (p < 99 ? p + 1 : p));
          }
        };

        xhr.onload = () => {
          try {
            if (xhr.status >= 200 && xhr.status < 300) {
              const data = JSON.parse(xhr.responseText || '{}');
              if (typeof data.authors === 'number') {
                setDatasetReady(true);
                setDatasetInfo({ authors: data.authors, totalMessages: data.totalMessages || 0 });
                setAnnouncements(a => [...a, `Dataset importé (${data.authors} auteurs, ${data.totalMessages || 0} messages)`]);
              }
              resolve(null);
            } else {
              let msg = `HTTP ${xhr.status}`;
              try {
                const data = JSON.parse(xhr.responseText || '{}');
                if (data && data.error) msg = data.error;
              } catch {
                /* ignore JSON parse error */
              }
              setAnnouncements(a => [...a, `Erreur upload CSV: ${msg}`]);
              reject(new Error(msg));
            }
          } catch (err) {
            setAnnouncements(a => [...a, `Erreur upload CSV: ${err.message}`]);
            reject(err);
          }
        };

        xhr.onerror = () => {
          setAnnouncements(a => [...a, 'Erreur upload CSV: réseau']);
          reject(new Error('network_error'));
        };

        xhr.onloadend = () => {
          setUploading(false);
          setUploadProgress(100);
          // après un court délai, réinitialiser visuellement
          setTimeout(() => setUploadProgress(0), 600);
        };

        xhr.send(csvText);
      });
    } catch (e) {
      setUploading(false);
      setAnnouncements(a => [...a, `Erreur upload CSV: ${e.message}`]);
    }
  }, [currentRoom]);

  // Scrape Discord cote serveur a partir de la selection (salons + auteurs)
  const scrapeDiscord = useCallback(async ({ channelIds, authorIds, label }) => {
    if (!currentRoom) throw new Error('no_room');
    const resp = await fetch(`${serverUrl}/api/rooms/${currentRoom}/discord-scrape`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'x-chef-id': socket.id || '' },
      body: JSON.stringify({ channelIds, authorIds, label }),
    });
    const data = await resp.json().catch(() => ({}));
    if (!resp.ok || !data.ok) {
      const msg = data?.error || `HTTP ${resp.status}`;
      setAnnouncements(a => [...a, `Erreur import Discord: ${msg}`]);
      throw new Error(msg);
    }
    setDatasetReady(true);
    setDatasetInfo({ authors: data.authors, totalMessages: data.totalMessages || 0 });
    setAnnouncements(a => [...a, `Messages Discord importés (${data.authors} auteurs, ${data.totalMessages || 0} messages)`]);
    return data;
  }, [currentRoom]);

  // Supprime une source (recombine le dataset cote serveur)
  const removeSource = useCallback(async (sourceId) => {
    if (!currentRoom || !sourceId) return;
    try {
      await fetch(`${serverUrl}/api/rooms/${currentRoom}/sources/${sourceId}`, {
        method: 'DELETE',
        headers: { 'x-chef-id': socket.id || '' },
      });
    } catch { /* le prochain roomState resynchronise */ }
  }, [currentRoom]);

  // Curation des auteurs (exclusions) : maj optimiste + POST autoritatif
  const setKeptAuthors = useCallback(async (labels) => {
    if (!currentRoom) return;
    setKeptAuthorsState(labels);
    try {
      const resp = await fetch(`${serverUrl}/api/rooms/${currentRoom}/authors-config`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-chef-id': socket.id || '' },
        body: JSON.stringify({ keptAuthors: labels }),
      });
      if (!resp.ok) throw new Error('http');
    } catch {
      /* le prochain roomState resynchronisera l'etat autoritatif */
    }
  }, [currentRoom]);

  const submitGuess = useCallback((guess, fraction) => {
    // fraction (position dans l'extrait) fournie par le mode mashup ; sinon guess simple.
    socket.emit('submitGuess', typeof fraction === 'number' ? { guess, fraction } : { guess });
    setHasGuessed(true);
    setMyGuess(guess);
    setPlayersGuessed(pg => (pg.includes(pseudo) ? pg : [...pg, pseudo]));
  }, [pseudo]);

  const restartLobby = useCallback(() => {
    socket.emit('restartLobby');
  }, []);

  const sendChatMessage = useCallback(message => {
    const safe = (message || '').toString().slice(0, 500);
    socket.emit('sendChatMessage', { message: safe });
  }, []);

  const leaveRoom = useCallback(() => {
    socket.emit('leaveRoom', () => {
      // ACK reçu: on peut nettoyer et laisser la navigation se faire côté UI
    });
    // reset local state
    setConnected(false);
    setGameStarted(false);
    setIsChef(false);
    setRoundNumber(0);
    setPhase('');
    setTimeLeft(0);
    setGuessOptions([]);
    setHasGuessed(false);
    setMyGuess(null);
    setPlayersGuessed([]);
    setGuessTicks([]);
    setClipUrl(null);
    setScores({});
    setMessages([]);
    setChatMessages([]);
    setAnnouncements([]);
    setLastAuthor(null);
    setLastProposals({});
    setLastRoundPoints({});
    setGameSettings(null);
    setCurrentRoom('');
    setFinalRanking([]);
  }, []);

  // ─── VALEURS EXPOSEES AU COMPOSANT ────────────────────────────────────────
  return {
    connected,
    gameStarted,
    isChef,
    chefName,
    roundNumber,
    phase,
    timeLeft,
    guessOptions,
  guessAvatars,
  guessNames,
    hasGuessed,
    myGuess,
    scores,
    playersGuessed,
    clipUrl,
    clipDurationSec,
    guessTicks,
    clipSync,
    clipProgress,
    lastMashupName,
    lastMashupAuthor,
    lastCorrectAnswer,
    chronoMessage,
    chronoMessages,
    chronoPeriod,
    lastAnswerFraction,
    blindtestPool,
    blindtestMaxAttempts,
    drawEnabled,
    emitDrawSegment,
    drawClearInfo,
    emitDrawClearVote,
    modeAccess,
    setModeAccess,
    studioAssign,
    studioInfo,
    studioItems,
    studioRanking,
    creationProgress,
    submitCreation,
    submitRating,
    discussionContext,
    discussionOptions,
    discussionReveal,
    discussionProgress,
    submitLie,
    submitVote,
    whoContext,
    whoKey,
    whoReveal,
    coverAssign,
    coverDraw,
    coverReveal,
    coverResult,
    coverUpvote,
    submitCover,
    submitUpvote,
    lobbyMode,
    selectMode,
    messages,
    announcements,
    lastAuthor,
    lastProposals,
    lastRoundPoints,
    gameSettings,
    currentRoom,
    joinRoom,
    startGame,
    stopGame,
    submitGuess,
    restartLobby,
    leaveRoom,
    sendChatMessage,
    finalRanking,
    chatMessages,
    datasetReady,
    datasetInfo,
    datasetAuthors,
    detectedNames,
    keptAuthors,
    setKeptAuthors,
    maxAuthors,
    datasetPeriod,
    sources,
    removeSource,
    eloChanges,
    uploadCsv,
    scrapeDiscord,
    uploading,
    uploadProgress,
  };
}
